import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { getTranslationByKey } from "../../../../utils/i18n";

export interface AggregatorOption {
  id: number;
  name: string;
  location?: number | string;
  physical_address?: string;
}

export interface AdminAreaNode {
  id: number | string;
  children?: AdminAreaNode[];
  [key: string]: unknown;
}

interface AggregatorSelectionProps {
  aggregators: AggregatorOption[];
  administrativeAreasTree: AdminAreaNode[];
  isFetching: boolean;
  selectedAggregatorId?: number;
  onSelect: (aggregator: AggregatorOption) => void;
  commodityLabel?: string;
  errorMessage?: string;
}

// Indexes the administrative-area tree so an aggregator's location id can be
// resolved to a readable "type: name, …" path. Ported from the legacy component.
function buildIndex(tree: AdminAreaNode[]) {
  const parentIndex = new Map<string, string>();
  const nodeIndex = new Map<string, AdminAreaNode>();

  function traverse(node: AdminAreaNode, parentId: string | null) {
    const id = String(node.id);
    nodeIndex.set(id, node);
    if (parentId !== null) {
      parentIndex.set(id, parentId);
    }
    node.children?.forEach(child => traverse(child, id));
  }

  tree.forEach(root => traverse(root, null));
  return { parentIndex, nodeIndex };
}

function getNodePath(
  { parentIndex, nodeIndex }: ReturnType<typeof buildIndex>,
  targetId: number | string,
  lang: string
) {
  const chain: string[] = [];
  let currentId: string | undefined = String(targetId);

  while (currentId) {
    const node = nodeIndex.get(currentId);
    if (!node) break;
    const type = getTranslationByKey(node, "type", lang);
    const name = getTranslationByKey(node, "name", lang);
    chain.unshift(`${type}: ${name}`);
    currentId = parentIndex.get(currentId);
  }

  return chain.join(", ");
}

// Single-select aggregator picker rendered as a radio list of cards. The demand
// payload still carries one `tagged_aggregator`; multi-select is future work.
export const AggregatorSelection = ({
  aggregators,
  administrativeAreasTree,
  isFetching,
  selectedAggregatorId,
  onSelect,
  commodityLabel,
  errorMessage,
}: AggregatorSelectionProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [query, setQuery] = useState("");

  const index = useMemo(() => buildIndex(administrativeAreasTree), [administrativeAreasTree]);

  const filteredAggregators = useMemo(() => {
    const search = query.trim().toLowerCase();
    return search
      ? aggregators.filter(aggregator => aggregator.name.toLowerCase().includes(search))
      : aggregators;
  }, [aggregators, query]);

  if (isFetching) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Spinner className="size-4" />
        {t("common.loading")}
      </div>
    );
  }

  if (!aggregators.length) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("marketOfferDetails.form.noAggregatorsFound")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {t("marketOfferDetails.form.aggregatorsSubtitle")}
      </p>
      <div className="relative">
        <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-muted-foreground" />
        <Input
          type="search"
          className="pl-9"
          placeholder={t("marketOfferDetails.form.search")}
          aria-label={t("marketOfferDetails.form.search")}
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </div>
      <RadioGroup
        value={selectedAggregatorId ? String(selectedAggregatorId) : undefined}
        onValueChange={value => {
          const aggregator = aggregators.find(a => String(a.id) === value);
          if (aggregator) {
            onSelect(aggregator);
          }
        }}
        aria-invalid={!!errorMessage}
        className="gap-0"
      >
        <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-x-3 border-b border-b-border pb-2 font-medium text-muted-foreground text-sm">
          <span className="size-6" aria-hidden />
          <span>{t("marketOfferDetails.form.aggregatorColumn")}</span>
          <span>{t("marketOfferDetails.form.matchingCommoditiesColumn")}</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {filteredAggregators.map(aggregator => {
            const path = aggregator.location
              ? getNodePath(index, aggregator.location, language)
              : "";
            const id = `aggregator-${aggregator.id}`;
            const isSelected = selectedAggregatorId === aggregator.id;
            return (
              <Label
                key={aggregator.id}
                htmlFor={id}
                className={cn(
                  "grid cursor-pointer grid-cols-[auto_1fr_1fr] px-2 items-center gap-x-3 border-b py-3 font-normal transition-colors hover:bg-accent",
                  isSelected && "bg-accent"
                )}
              >
                <RadioGroupItem id={id} value={String(aggregator.id)} />
                <div>
                  <div className="font-medium text-foreground">{aggregator.name}</div>
                  {path && <div className="text-muted-foreground text-xs">{path}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {commodityLabel && <Badge variant="secondary">{commodityLabel}</Badge>}
                </div>
              </Label>
            );
          })}
          {filteredAggregators.length === 0 && (
            <p className="py-3 text-muted-foreground text-sm">
              {t("marketOfferDetails.form.noAggregatorsFound")}
            </p>
          )}
        </div>
      </RadioGroup>
      {errorMessage && (
        <p role="alert" className="text-destructive text-sm">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
